/**
 * Web Audio graph for F1 host capture.
 *
 * Wires: MediaStream → AudioContext source → AudioWorkletNode('pcm-worklet').
 * The worklet downsamples to 16 kHz mono and posts PCM16 frames; this module
 * forwards them (plus a per-frame RMS energy reading for client-side VAD) to a
 * caller-supplied handler.
 *
 * TODO(opus): frames are raw PCM16 for F1. Opus encoding is a later bandwidth
 * optimization (PRD §8 #3) — it would slot in here or inside the worklet.
 */

/** Target uplink format — matches the server-side ring buffer (PRD §4). */
export const TARGET_SAMPLE_RATE = 16_000;
/** 40 ms frame at 16 kHz = 640 samples. Balances latency vs frame overhead. */
export const FRAME_SAMPLES = 640;

const WORKLET_URL = '/pcm-worklet.js';

/** A single downsampled PCM16 frame plus its energy reading. */
export interface PcmFrame {
  /** PCM16 little-endian samples, length === FRAME_SAMPLES. */
  pcm: ArrayBuffer;
  /** Root-mean-square amplitude in [0, 1] — feeds the VAD gate. */
  rms: number;
}

export type PcmFrameHandler = (frame: PcmFrame) => void;

interface WorkletMessage {
  pcm: ArrayBuffer;
  rms: number;
}

/**
 * Owns the AudioContext + worklet node for one capture session. Construct,
 * `start(stream, onFrame)`, then `stop()` to tear the graph down.
 */
export class AudioWorkletCapture {
  private context: AudioContext | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private node: AudioWorkletNode | null = null;

  /** True while an audio graph is live. */
  get isRunning(): boolean {
    return this.context !== null;
  }

  /**
   * Build the graph from a MediaStream and begin emitting frames. The stream's
   * lifecycle is owned by the caller (captureController) — stop() does NOT stop
   * the stream's tracks.
   */
  async start(stream: MediaStream, onFrame: PcmFrameHandler): Promise<void> {
    if (this.context) {
      throw new Error('AudioWorkletCapture already started.');
    }

    const context = new AudioContext();
    // A context may start suspended (autoplay policy) — resume on user gesture.
    if (context.state === 'suspended') {
      await context.resume();
    }

    await context.audioWorklet.addModule(WORKLET_URL);

    const source = context.createMediaStreamSource(stream);
    const node = new AudioWorkletNode(context, 'pcm-worklet', {
      numberOfInputs: 1,
      numberOfOutputs: 0,
      channelCount: 1,
      channelCountMode: 'explicit',
      processorOptions: {
        targetSampleRate: TARGET_SAMPLE_RATE,
        frameSamples: FRAME_SAMPLES,
      },
    });

    node.port.onmessage = (ev: MessageEvent<WorkletMessage>) => {
      onFrame({ pcm: ev.data.pcm, rms: ev.data.rms });
    };

    source.connect(node);

    this.context = context;
    this.source = source;
    this.node = node;
  }

  /** Tear down the graph. Safe to call more than once. */
  async stop(): Promise<void> {
    if (this.node) {
      this.node.port.onmessage = null;
      this.node.disconnect();
      this.node = null;
    }
    if (this.source) {
      this.source.disconnect();
      this.source = null;
    }
    if (this.context) {
      await this.context.close().catch(() => undefined);
      this.context = null;
    }
  }
}
